import AppContext from 'App/AppContext';
import TemplateBindingModal from 'components/OutputLocations/TemplateBindingModal';
import DocumentOutputLocation, { DocumentOutputPurpose } from 'models/documentOutputLocation';
import OutputLocationBinding, { OutputLocationBindings, OutputLocationScope } from 'models/outputLocationBinding';
import Task from 'models/tasks/task';
import { useContext, useEffect, useMemo, useState } from 'react';
import { apiGetOutputLocations } from 'services/Api/documentOutputLocationService';
import { apiRequest } from 'services/Auth/authConfig';

interface ITemplateEvidenceLocationModalProps {
  isOpen: boolean;
  // The saved template whose evidence settings are edited.
  task: Task;
  onClose: () => void;
}

/**
 * The evidence settings of one template, opened from the "..." menu of the template editor: the modal the Reports
 * tab opens for a template, with the template's own row or an empty one when it has none yet.
 */
const TemplateEvidenceLocationModal = (props: ITemplateEvidenceLocationModalProps) => {
  const appContext = useContext(AppContext);
  const [location, setLocation] = useState<DocumentOutputLocation | undefined>(undefined);
  const [bindings, setBindings] = useState<OutputLocationBindings | undefined>(undefined);

  useEffect(() => {
    if (props.isOpen) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const load = async () => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const locations = await apiGetOutputLocations(accessToken);
      setLocation(locations.find((item) => item.purpose === DocumentOutputPurpose.EvidenceUploads));
      setBindings(await appContext.globalDataCache.outputLocationBindings.getItems(true));
    } catch (err) {
      appContext.setError(err);
      props.onClose();
    }
  };

  //one row per load: the modal resets its fields whenever the row it gets changes
  const binding = useMemo((): OutputLocationBinding | undefined => {
    if (!bindings) return undefined;
    const own = bindings.templates.find((row) => row.templateTaskId === props.task.taskId);
    if (own) return own;

    const empty = new OutputLocationBinding();
    empty.scope = OutputLocationScope.TaskTemplate;
    empty.templateTaskId = props.task.taskId;
    empty.templateName = props.task.name;
    empty.templateTaskType = props.task.taskType;

    return empty;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bindings]);

  if (!location || !bindings || !binding) return null;

  return (
    <TemplateBindingModal
      isOpen={props.isOpen}
      binding={binding}
      location={location}
      defaultFolderRule={bindings.default.effective.folderRule}
      defaultFolderNameFormat={bindings.default.effective.folderNameFormat}
      tokens={bindings.tokens}
      onClose={props.onClose}
      onSaved={() => appContext.globalDataCache.outputLocationBindings.invalidate()}
    />
  );
};

export default TemplateEvidenceLocationModal;
